import type { NavigateFunction } from 'react-router-dom'

/**
 * Factory for the `onNavigate` callback passed to a federated child app
 * running in window-mode (MemoryRouter via `DynamicBridgeRouter`).
 *
 * The child's `RouterLocationOutbound` emits the basename-stripped pathname
 * (e.g. `/dashboard`); the container's outer browser router lives at
 * `${appPath}/*` (e.g. `/hr/*`). Without prefixing, calling
 * `navigate(emitted)` pushes the bare path and the outer router falls out
 * of the app's route entirely.
 *
 * Returns a stable shape only when `navigate` and `appPath` are stable —
 * memoize with `useCallback`/`useMemo` at the call site if you need
 * referential equality.
 *
 * Usage:
 *   const navigate = useNavigate()
 *   const onNavigate = useMemo(
 *     () => createChildAppOnNavigate(apps[AppId.HR].path, navigate),
 *     [navigate, apps]
 *   )
 */
export const createChildAppOnNavigate =
  (appPath: string, navigate: NavigateFunction) =>
  ({ pathname: nextPathname }: { pathname: string }) => {
    const fullPath = nextPathname.startsWith(appPath)
      ? nextPathname
      : `${appPath}${nextPathname === '/' ? '' : nextPathname}`

    if (window.location.pathname !== fullPath) {
      navigate(fullPath)
    }
  }
